import React, { useState } from "react";
import {
  Box,
  Button,
  Grid,
  Typography,
  Popover,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
} from "@mui/material";
import { Layout } from "../Layouts/Layout";
import { TemplateCard } from "../Components/Template/Card";
import { useNavigate } from "react-router";
import { Add } from "@mui/icons-material";
import { Template, TemplateType } from "../types";
import {
  ADD_FORM,
  CLONE_FORM,
  DELETE_FORM,
  GET_FORMS,
  AddFormResult,
  CloneFormResult,
  FormsResult,
} from "../Queries";
import { useTranslation } from "react-i18next";
import { useMutation, useQuery } from "@apollo/client";
import { useErrorBoundary } from "../Contexts/ErrorContext";
import { FEATURES } from "../features";

export const Templates = () => {
  const navigate = useNavigate();
  const [t] = useTranslation();
  const { setError } = useErrorBoundary();
  const [templateTypeFilter, setTemplateTypeFilter] = useState<
    TemplateType | ""
  >("");
  const [addButtonAnchor, setAddButtonAnchor] = useState<null | HTMLElement>(
    null,
  );

  //
  // Queries
  //

  const { loading: formsLoading, data: formsData } = useQuery<FormsResult>(
    GET_FORMS,
    {
      variables: { filters: {} },
      onError: (error) => setError(t("errors.forms.list"), error),
    },
  );

  //
  // Mutations
  //

  const [addForm] = useMutation<AddFormResult>(ADD_FORM, {
    refetchQueries: ["GetForms"],
    onError: (error) => setError(t("errors.forms.create"), error),
  });

  const [cloneForm] = useMutation<CloneFormResult>(CLONE_FORM, {
    refetchQueries: ["GetForms"],
    onError: (error) => setError(t("errors.forms.clone"), error),
  });

  const [deleteForm] = useMutation<boolean>(DELETE_FORM, {
    refetchQueries: ["GetForms"],
    onError: (error) => setError(t("errors.forms.delete"), error),
  });

  // TODO: shift this filtering to backend!!!!
  const templates = formsData
    ? formsData.forms.filter(
        (form) =>
          !form.isSnapshot &&
          (form.isMine ||
            form.isDirectShare ||
            form.isShared ||
            form.isGlobal) &&
          (!templateTypeFilter || form.templateType === templateTypeFilter),
      )
    : [];

  //
  // Event Handlers
  //

  const handleAddTemplate = async (templateType: TemplateType) => {
    setAddButtonAnchor(null);
    const result = await addForm({
      variables: {
        name: "New template",
        templateType,
      },
    });

    if (result.data?.addForm && "id" in result.data.addForm) {
      navigate(`/templates/${result.data.addForm.id}`);
    }
  };

  const handleCloneTemplate = async (id: string) => {
    const result = await cloneForm({
      variables: {
        id,
      },
    });

    if (result.data?.cloneForm && "id" in result.data.cloneForm) {
      navigate(`/templates/${result.data.cloneForm.id}`);
    }
  };

  const handleDeleteTemplate = async (id: string) => {
    await deleteForm({
      variables: {
        id,
      },
    });
  };

  const showLoading = formsLoading;
  const yourTemplates = templates.filter((template) => template.isMine);
  const institutionTemplates = templates.filter(
    (template) => template.isShared && !template.isMine && !template.isGlobal,
  );
  const globalTemplates = templates.filter(
    (template) => template.isGlobal && !template.isMine && !template.isShared,
  );

  return (
    <Layout
      breadcrumbs={[{ title: t("breadcrumbs.templates") }]}
      loading={showLoading}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          mb: 2,
          alignItems: "center",
        }}
      >
        <Box sx={{ minWidth: 200 }}>
          {FEATURES.SUBMISSION_REVIEW_ENABLED && (
            <FormControl fullWidth>
              <InputLabel>Type</InputLabel>
              <Select
                value={templateTypeFilter}
                label="Type"
                onChange={(e) =>
                  setTemplateTypeFilter(e.target.value as TemplateType | "")
                }
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value={TemplateType.Survey}>
                  Survey Templates
                </MenuItem>
                <MenuItem value={TemplateType.Rubric}>Rubrics</MenuItem>
              </Select>
            </FormControl>
          )}
        </Box>
        <Box>
          <Button
            variant="contained"
            aria-label="add template"
            endIcon={<Add />}
            onClick={(e) => {
              if (FEATURES.SUBMISSION_REVIEW_ENABLED) {
                setAddButtonAnchor(e.currentTarget);
              } else {
                handleAddTemplate(TemplateType.Survey);
              }
            }}
          >
            {t("templates.add")}
          </Button>
          {FEATURES.SUBMISSION_REVIEW_ENABLED && (
            <Popover
              open={Boolean(addButtonAnchor)}
              anchorEl={addButtonAnchor}
              onClose={() => setAddButtonAnchor(null)}
              anchorOrigin={{
                vertical: "bottom",
                horizontal: "right",
              }}
              transformOrigin={{
                vertical: "top",
                horizontal: "right",
              }}
            >
              <MenuItem onClick={() => handleAddTemplate(TemplateType.Survey)}>
                Add Survey Template
              </MenuItem>
              <MenuItem onClick={() => handleAddTemplate(TemplateType.Rubric)}>
                Add Rubric
              </MenuItem>
            </Popover>
          )}
        </Box>
      </Box>

      <Typography
        variant="h5"
        color="primary"
        sx={{ fontWeight: "bold", mb: 1 }}
      >
        {t("templates.your")}
      </Typography>
      <Grid container spacing={2}>
        {yourTemplates.map((template) => (
          <Grid item key={template.id} xs={12} md={6} lg={4}>
            <TemplateCard
              template={template}
              onClone={() => handleCloneTemplate(template.id)}
              onDelete={() => handleDeleteTemplate(template.id)}
            />
          </Grid>
        ))}
      </Grid>

      {institutionTemplates.length > 0 && (
        <>
          <Typography
            variant="h5"
            color="primary"
            sx={{ fontWeight: "bold", mb: 1, mt: 2 }}
          >
            {t("templates.institution")}
          </Typography>
          <Grid container spacing={2}>
            {institutionTemplates.map((template) => (
              <Grid item key={template.id} xs={12} md={6} lg={4}>
                <TemplateCard
                  template={template}
                  onClone={() => handleCloneTemplate(template.id)}
                />
              </Grid>
            ))}
          </Grid>
        </>
      )}

      {globalTemplates.length > 0 && (
        <>
          <Typography
            variant="h5"
            color="primary"
            sx={{ fontWeight: "bold", mb: 1, mt: 2 }}
          >
            {t("templates.global")}
          </Typography>
          <Grid container spacing={2}>
            {globalTemplates.map((template) => (
              <Grid item key={template.id} xs={12} md={6} lg={4}>
                <TemplateCard
                  template={template}
                  onClone={() => handleCloneTemplate(template.id)}
                />
              </Grid>
            ))}
          </Grid>
        </>
      )}
    </Layout>
  );
};
